import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Panel, PanelHeader } from '@/components/ui/Panel';
import StatusBadge from '@/components/ui/StatusBadge';
import { ApiError } from '@/lib/api';
import { onboardingApi, siteScanApi, type PartnerFull, type SiteProductRow, type SiteScanState } from '@/lib/onboarding';
import { btnGhost, btnPrimary, inputCls } from '../onboarding/components/styles';

interface Props {
  partner: PartnerFull;
  canEdit: boolean;
  onPartner: (p: PartnerFull) => void;
  onToast: (message: string, tone?: 'success' | 'error') => void;
  onApplied?: () => void;
  /** full — панель + таблица карточек сайта (вкладка «Сайт»); compact — только панель (обзор). */
  mode: 'compact' | 'full';
}

/** Сайт поставщика (29.10.2026): адрес, запуск разбора, ход и итоги, карточки с сайта и их привязка к артикулам,
 * кнопка «Подтянуть в превью» — фото, описания, характеристики и цена сайта (RRP) уходят в листинги. */
export default function SitePanel({ partner, canEdit, onPartner, onToast, onApplied, mode }: Props) {
  const { t, i18n } = useTranslation();
  const [website, setWebsite] = useState(partner.websiteUrl ?? '');
  const [state, setState] = useState<SiteScanState | null>(null);
  const [rows, setRows] = useState<SiteProductRow[] | null>(null);
  const [filter, setFilter] = useState<'all' | 'matched' | 'unmatched'>('all');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await siteScanApi.state(partner.id));
      if (mode === 'full') setRows(await siteScanApi.products(partner.id));
    } catch {
      setState(null);
    }
  }, [partner.id, mode]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!state?.running) return;
    const id = window.setInterval(() => void load(), 3000);
    return () => window.clearInterval(id);
  }, [state?.running, load]);
  useEffect(() => setWebsite(partner.websiteUrl ?? ''), [partner.websiteUrl]);

  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
  const err = (e: unknown) => onToast(t('onb.error', { code: (e as ApiError).code }), 'error');

  const saveWebsite = async () => {
    setBusy(true);
    try {
      onPartner(await onboardingApi.patchPartner(partner.id, { websiteUrl: website.trim() || null }));
      onToast(t('pc.websiteSaved'));
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  };
  const start = async () => {
    setBusy(true);
    try {
      const url = website.trim();
      await siteScanApi.start(partner.id, url || undefined);
      if (url !== (partner.websiteUrl ?? '')) onPartner({ ...partner, websiteUrl: url });
      onToast(t('pc.scanStarted'));
      await load();
    } catch (e) {
      const code = (e as ApiError).code;
      onToast(code === 'already_running' ? t('pc.scanRunning') : code === 'url_required' ? t('pc.urlRequired') : t('onb.error', { code }), 'error');
    } finally {
      setBusy(false);
    }
  };
  const apply = async (overwriteImages = false) => {
    setBusy(true);
    try {
      const r = await siteScanApi.apply(partner.id, { overwriteImages });
      onToast(t('pc.applied', { count: r.applied }));
      await load();
      onApplied?.();
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  };
  const setMatch = async (row: SiteProductRow, article: string | null) => {
    setEditing(null);
    try {
      const upd = await siteScanApi.setMatch(partner.id, row.id, article);
      setRows((rs) => rs?.map((r) => (r.id === row.id ? upd : r)) ?? null);
    } catch (e) {
      err(e);
    }
  };

  const last = state?.last ?? null;
  const tone = last?.status === 'done' ? 'primary' : last?.status === 'failed' ? 'danger' : last ? 'accent' : 'neutral';
  const visible = (rows ?? []).filter((r) => filter === 'all' || (filter === 'matched' ? r.matchedArticle : !r.matchedArticle));

  return (
    <div className="flex flex-col gap-4">
      <Panel>
        <PanelHeader title={t('pc.websiteTitle')} subtitle={t('pc.websiteSub2')} />
        <div className="flex flex-col gap-2 border-t border-background-200 pt-3 sm:flex-row">
          <div className="relative min-w-0 flex-1">
            <i className="ri-global-line pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[14px] text-foreground-400" />
            <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" disabled={!canEdit} className={`${inputCls} pl-8`} />
          </div>
          {canEdit && website.trim() !== (partner.websiteUrl ?? '') && (
            <button type="button" onClick={saveWebsite} disabled={busy} className={btnGhost}>
              <i className="ri-save-line text-[14px]" /> {t('onb.save')}
            </button>
          )}
          {canEdit && (
            <button type="button" onClick={start} disabled={busy || !website.trim() || Boolean(state?.running)} className={btnPrimary}>
              <i className={`${state?.running ? 'ri-loader-4-line animate-spin' : 'ri-radar-line'} text-[14px]`} /> {state?.running ? t('pc.scanRunning') : last ? t('pc.rescan') : t('pc.scan')}
            </button>
          )}
          {partner.websiteUrl && (
            <a href={partner.websiteUrl} target="_blank" rel="noreferrer" className={btnGhost} title={t('pc.openSite')}>
              <i className="ri-external-link-line text-[14px]" />
            </a>
          )}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-background-200 pt-3 sm:grid-cols-4">
          {[
            { label: t('pc.scanPages'), value: last ? String(last.pagesSeen) : '—' },
            { label: t('pc.scanProducts'), value: state ? String(state.products.total) : '—' },
            { label: t('pc.scanMatched'), value: state ? String(state.products.matched) : '—' },
            { label: t('pc.scanApplied'), value: state ? String(state.products.applied) : '—' },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-foreground-400">{s.label}</p>
              <p className="tnum mt-0.5 text-base font-semibold text-foreground-950">{s.value}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11.5px] text-foreground-500">
          {last ? (
            <>
              <StatusBadge tone={tone}>{t(`pc.scanStatus.${last.status}`)}</StatusBadge>
              <span>{t('pc.lastScan', { date: fmt(last.finishedAt || last.startedAt || last.createdAt) })}</span>
              {last.error && <span className="text-rose-600">{last.error}</span>}
            </>
          ) : (
            <span>{t('pc.neverScanned')}</span>
          )}
          {canEdit && state && state.products.matched > 0 && !state.running && (
            <span className="ml-auto flex gap-2">
              <button type="button" onClick={() => apply(false)} disabled={busy} className={btnGhost} title={t('pc.applyToPreviewHint')}>
                <i className="ri-download-2-line text-[13px]" /> {t('pc.applyToPreview')}
              </button>
              <button type="button" onClick={() => apply(true)} disabled={busy} className={btnGhost} title={t('pc.applyOverwriteHint')}>
                <i className="ri-image-edit-line text-[13px]" /> {t('pc.applyOverwrite')}
              </button>
            </span>
          )}
        </div>
        <p className="mt-2 text-[11.5px] leading-5 text-foreground-500">{t('pc.websiteNote2')}</p>
        {mode === 'compact' && state && state.products.total > 0 && (
          <Link to={`/partners/${partner.id}?tab=site`} className="mt-1 inline-block text-[12px] font-semibold text-primary-700">{t('pc.openSiteTab', { count: state.products.total })}</Link>
        )}
        {last && last.log.length > 0 && mode === 'full' && <p className="mt-2 text-[10.5px] leading-4 text-foreground-400">{last.log.slice(-3).join(' · ')}</p>}
      </Panel>

      {mode === 'full' && (
        <Panel padded={false}>
          <div className="flex flex-col gap-2 border-b border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <PanelHeader title={t('pc.siteProductsTitle')} subtitle={t('pc.siteProductsSub')} className="!mb-0" />
            <div className="flex gap-1 rounded-md border border-background-200 bg-background-100 p-1">
              {(['all', 'matched', 'unmatched'] as const).map((f) => (
                <button key={f} type="button" onClick={() => setFilter(f)} className={`cursor-pointer rounded px-2 py-1 text-[11px] font-medium ${filter === f ? 'bg-background-50 text-foreground-950' : 'text-foreground-500'}`}>
                  {t(`pc.siteFilter.${f}`)} <span className="tnum text-foreground-400">{f === 'all' ? rows?.length ?? 0 : (rows ?? []).filter((r) => (f === 'matched' ? r.matchedArticle : !r.matchedArticle)).length}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full min-w-[860px] border-collapse text-[12px]">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/70 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                  <th className="px-4 py-2 font-medium">{t('pc.colSiteProduct')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('pc.colSitePrice')}</th>
                  <th className="px-3 py-2 font-medium">{t('pc.colSiteAvail')}</th>
                  <th className="px-3 py-2 font-medium">{t('pc.colMatched')}</th>
                  <th className="px-4 py-2 font-medium">{t('pc.colApplied')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id} className="border-b border-background-200/70 last:border-0">
                    <td className="px-4 py-2 align-top">
                      <div className="flex items-center gap-2">
                        {r.images[0] ? <img src={r.images[0]} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded border border-background-200 bg-white object-contain" /> : <span className="h-10 w-10 shrink-0 rounded bg-background-100" />}
                        <div className="min-w-0 max-w-[380px]">
                          <a href={r.url} target="_blank" rel="noreferrer" className="block truncate font-medium text-foreground-900 hover:text-primary-700">{r.title || r.url}</a>
                          <p className="truncate text-[10.5px] text-foreground-400">{r.siteSku || r.url.replace(/^https?:\/\/[^/]+/, '')}</p>
                        </div>
                      </div>
                    </td>
                    <td className="tnum px-3 py-2 text-right align-top text-foreground-700">{r.price != null ? `${r.price.toLocaleString('en-AE')} ${r.currency ?? ''}` : '—'}</td>
                    <td className="px-3 py-2 align-top text-foreground-600">{r.availability ?? '—'}</td>
                    <td className="px-3 py-2 align-top">
                      {editing === r.id ? (
                        <input autoFocus defaultValue={r.matchedArticle ?? ''} onBlur={(e) => void setMatch(r, e.target.value.trim() || null)} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} className="h-7 w-40 rounded border border-primary-400 bg-background-50 px-1.5 text-[11.5px] outline-none" placeholder={t('pc.matchPlaceholder')} />
                      ) : (
                        <button type="button" disabled={!canEdit} onClick={() => setEditing(r.id)} className={`cursor-pointer rounded px-1.5 py-0.5 text-[11.5px] font-medium hover:bg-background-100 ${r.matchedArticle ? 'text-primary-700' : 'text-foreground-400'}`} title={t('pc.matchHint')}>
                          {r.matchedArticle ? `${r.matchedArticle}${r.matchedSku ? ` → ${r.matchedSku}` : ''}` : t('pc.noMatch')}
                          {r.matchScore != null && r.matchScore < 1 && <span className="tnum ml-1 text-[10px] text-foreground-400">{Math.round(r.matchScore * 100)}%</span>}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-[11px] text-foreground-500">{r.appliedAt ? fmt(r.appliedAt) : '—'}</td>
                  </tr>
                ))}
                {rows && visible.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-[12px] text-foreground-500">{t('pc.noSiteProducts')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}
